/**
 * Clay Universes
 *
 * Only the default collection panel is rendered by Liquid. Every other panel is pulled from
 * sections/clay-universe-collection.liquid through the Section Rendering API, and Lottie
 * animations are fetched as separate JSON instead of being inlined into the page.
 *
 * Character dialogs are not in the markup either: each character button carries its copy in
 * data-* attributes and one shared dialog is populated on click.
 */
(function () {
  'use strict';

  var MOBILE_WIDTH = 768;
  var CHARACTER_TIMER = 2000;
  var MODAL_ID = 'hc-worlds-character-modal';
  var SWIPER_WAIT_ATTEMPTS = 40;
  var SWIPER_WAIT_INTERVAL = 100;

  /* -------------------------------------------------------------------------------- helpers */

  function normalizeColor(color) {
    if (!color) return '';
    return color.charAt(0) === '#' ? color : '#' + color;
  }

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function parseJson(text) {
    if (!text) return null;

    try {
      var value = JSON.parse(text);
      // A json metafield can come back double-encoded depending on how it was written.
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch (_error) {
      return null;
    }
  }

  // basePath lets a collection route rewrite the path to the world being shown, so the URL is
  // the world's real page and a reload server-renders it instead of falling back to ?cid.
  function updateUrl(params, basePath) {
    var url = new URL(window.location.href);

    if (basePath) url.pathname = basePath;

    Object.keys(params).forEach(function (key) {
      if (params[key]) url.searchParams.set(key, params[key]);
      else url.searchParams.delete(key);
    });

    window.history.replaceState({}, '', url.toString());
  }

  function onIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 3000 });
    } else {
      window.setTimeout(callback, 1200);
    }
  }

  // Swiper is loaded by the theme, so poll for it instead of blocking everything else on it.
  function waitForSwiper() {
    return new Promise(function (resolve) {
      var attempts = SWIPER_WAIT_ATTEMPTS;

      (function poll() {
        if (window.Swiper || attempts <= 0) {
          resolve(window.Swiper || null);
          return;
        }
        attempts -= 1;
        window.setTimeout(poll, SWIPER_WAIT_INTERVAL);
      })();
    });
  }

  /* ------------------------------------------------------------------ section rendering api */

  function fetchSectionDocument(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed with ' + response.status);
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /* ------------------------------------------------------------------------------- lottie */

  var lottieLibPromise = null;
  var animationDataRequests = new Map();

  function loadLottieLib(src) {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (!src) return Promise.resolve(null);

    if (!lottieLibPromise) {
      lottieLibPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = function () {
          resolve(window.lottie || null);
        };
        script.onerror = function () {
          reject(new Error('lottie-web failed to load'));
        };
        document.head.appendChild(script);
      }).catch(function (error) {
        lottieLibPromise = null;
        throw error;
      });
    }

    return lottieLibPromise;
  }

  function fetchAnimationData(trigger) {
    var key = trigger.dataset.collectionHandle;
    if (animationDataRequests.has(key)) return animationDataRequests.get(key);

    var directUrl = trigger.dataset.lottieUrl;
    var sectionUrl = trigger.dataset.lottieSectionUrl;
    var request;

    if (directUrl) {
      request = fetch(directUrl).then(function (response) {
        return response.ok ? response.json() : null;
      });
    } else if (sectionUrl) {
      request = fetchSectionDocument(sectionUrl).then(function (doc) {
        var payload = doc.querySelector('[data-worlds-lottie-payload]');
        return payload ? parseJson(payload.textContent) : null;
      });
    } else {
      request = Promise.resolve(null);
    }

    request = request.catch(function () {
      return null;
    });

    animationDataRequests.set(key, request);
    return request;
  }

  /* ------------------------------------------------------------------- deferred image reveal */

  // The theme's images use native loading="lazy" with a real src. An image parsed inside a
  // display:none subtree has no layout box, so the browser never brings it in — and panels are
  // inserted hidden, which is why product grids stayed blank. Flipping loading to eager and
  // re-setting the source re-runs the browser's "update the image data" step, which is what
  // actually clears a deferred load.

  var imageObserver = null;

  function eagerLoadImage(img) {
    if (img.dataset.worldsEager === 'true') return;
    img.dataset.worldsEager = 'true';
    img.setAttribute('loading', 'eager');

    if (img.complete && img.naturalWidth) return;

    var srcset = img.getAttribute('srcset');
    var src = img.getAttribute('src');
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
  }

  function isNearViewport(el) {
    var rect = el.getBoundingClientRect();
    if (!rect.width && !rect.height) return false;
    return rect.top < window.innerHeight + 600 && rect.bottom > -600;
  }

  // Anything still off screen keeps its lazy behaviour, just driven by an observer that knows
  // about elements added after page load.
  function ensureImageObserver() {
    if (imageObserver || !('IntersectionObserver' in window)) return imageObserver;

    imageObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          // A display:none image reports a 0,0,0,0 rect, which the root margin counts as
          // intersecting. Leave it observed so its own set stays lazy until it is shown.
          var rect = entry.boundingClientRect;
          if (!rect.width && !rect.height) return;

          imageObserver.unobserve(entry.target);
          eagerLoadImage(entry.target);
        });
      },
      { rootMargin: '600px 0px' }
    );

    return imageObserver;
  }

  // [data-aos] elements sit at opacity 0 until the scroll-animation library registers them, and
  // it only scans the DOM it saw at startup — so a fetched panel's images can load and still be
  // invisible. <image-element data-aos="image-fade-in"> is the theme's image wrapper.
  function refreshScrollAnimations(scope) {
    if (window.AOS && typeof window.AOS.refreshHard === 'function') window.AOS.refreshHard();

    scope.querySelectorAll('[data-aos]').forEach(function (el) {
      if (isNearViewport(el)) el.classList.add('aos-animate');
    });
  }

  // Hold a skeleton in the image's frame until it has actually decoded. Applied from JS rather
  // than Liquid so that an already-cached image never flashes a placeholder, and so rendering
  // without JS is unchanged.
  function trackImageFrame(img) {
    var frame = img.closest('[data-worlds-image-frame]');
    if (!frame || frame.dataset.worldsFrameTracked === 'true') return;
    frame.dataset.worldsFrameTracked = 'true';

    // complete covers "already decoded" and "already failed" — both mean: stop waiting.
    if (img.complete) {
      frame.classList.remove('is-loading');
      return;
    }

    frame.classList.add('is-loading');

    var settle = function () {
      frame.classList.remove('is-loading');
      img.removeEventListener('load', settle);
      img.removeEventListener('error', settle);
    };

    img.addEventListener('load', settle);
    img.addEventListener('error', settle);
  }

  function revealImages(scope) {
    if (!scope) return;

    var observer = ensureImageObserver();

    scope.querySelectorAll('img').forEach(function (img) {
      trackImageFrame(img);

      if (img.dataset.worldsEager === 'true') return;
      if (img.getAttribute('loading') !== 'lazy') return;
      if (img.complete && img.naturalWidth) return;

      if (isNearViewport(img)) eagerLoadImage(img);
      else if (observer) observer.observe(img);
      else eagerLoadImage(img);
    });

    refreshScrollAnimations(scope);
  }

  /* ---------------------------------------------------------------- shared character dialog */

  var sharedModal = null;
  var lastModalTrigger = null;

  function closeCharacterModal() {
    if (!sharedModal || sharedModal.hidden) return;

    sharedModal.classList.remove('modal--is-active', 'is-active');
    sharedModal.setAttribute('aria-hidden', 'true');
    sharedModal.hidden = true;

    document.documentElement.classList.remove('modal-open');
    document.body.classList.remove('modal-open');

    if (lastModalTrigger && document.body.contains(lastModalTrigger)) lastModalTrigger.focus();
    lastModalTrigger = null;
  }

  function getSharedModal() {
    if (sharedModal && document.body.contains(sharedModal)) return sharedModal;

    sharedModal = document.createElement('div');
    sharedModal.id = MODAL_ID;
    sharedModal.className = 'modal modal--square modal--worlds-character';
    sharedModal.setAttribute('role', 'dialog');
    sharedModal.setAttribute('aria-modal', 'true');
    sharedModal.setAttribute('aria-hidden', 'true');
    sharedModal.setAttribute('aria-labelledby', MODAL_ID + '-title');
    sharedModal.hidden = true;
    sharedModal.innerHTML = [
      '<div class="modal__inner">',
      '<div class="modal__centered">',
      '<div class="modal__centered-content">',
      '<button type="button" class="modal__close js-modal-close" data-worlds-modal-close aria-label="Close">',
      '<span aria-hidden="true">&times;</span>',
      '</button>',
      '<div class="hc-worlds-character-modal" data-worlds-modal-grid>',
      '<div class="hc-worlds-character-modal__image" data-worlds-modal-image data-worlds-image-frame hidden>' +
        '<img alt="" decoding="async"></div>',
      '<div class="hc-worlds-character-modal__content">',
      '<h2 id="' + MODAL_ID + '-title" class="hc-worlds-character-modal__title" data-worlds-modal-title></h2>',
      '<div class="hc-worlds-character-modal__description" data-worlds-modal-description></div>',
      '</div>',
      '</div>',
      '</div>',
      '</div>',
      '</div>'
    ].join('');

    sharedModal.querySelector('[data-worlds-modal-close]').addEventListener('click', closeCharacterModal);

    sharedModal.addEventListener('click', function (event) {
      var clickedBackdrop =
        event.target === sharedModal ||
        event.target.classList.contains('modal__inner') ||
        event.target.classList.contains('modal__centered');

      if (clickedBackdrop) closeCharacterModal();
    });

    document.body.appendChild(sharedModal);
    return sharedModal;
  }

  function setModalImage(wrap, image, url, alt) {
    image.onload = null;
    image.onerror = null;

    if (!url) {
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.alt = '';
      wrap.hidden = true;
      wrap.classList.remove('is-loading');
      return;
    }

    wrap.hidden = false;
    image.alt = alt || '';

    // Reopening the same character: the frame on screen is already the right one.
    if (image.getAttribute('src') === url) {
      wrap.classList.remove('is-loading');
      return;
    }

    // Drop the previous character's frame first — otherwise the browser keeps painting it until
    // the new image has decoded, which reads as "the modal shows the wrong character".
    image.removeAttribute('src');
    wrap.classList.add('is-loading');

    image.onload = function () {
      wrap.classList.remove('is-loading');
    };
    image.onerror = function () {
      wrap.classList.remove('is-loading');
      wrap.hidden = true;
    };

    image.src = url;
    if (image.complete && image.naturalWidth) wrap.classList.remove('is-loading');
  }

  function openCharacterModal(trigger) {
    var modal = getSharedModal();
    var grid = modal.querySelector('[data-worlds-modal-grid]');
    var imageWrap = modal.querySelector('[data-worlds-modal-image]');
    var image = imageWrap.querySelector('img');
    var title = modal.querySelector('[data-worlds-modal-title]');
    var description = modal.querySelector('[data-worlds-modal-description]');

    var name = trigger.dataset.characterName || '';
    var color = normalizeColor(trigger.dataset.characterColor);
    var imageUrl = trigger.dataset.characterImage || '';

    title.textContent = name;
    title.style.color = color || '';
    // Same markup Liquid used to emit for this description (newline_to_br), merchant-authored.
    description.innerHTML = trigger.dataset.characterDescription || '';

    setModalImage(imageWrap, image, imageUrl, name);

    if (imageUrl) grid.removeAttribute('data-no-image');
    else grid.setAttribute('data-no-image', '');

    lastModalTrigger = trigger;
    trigger.setAttribute('aria-controls', MODAL_ID);

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('modal--is-active', 'is-active');

    document.documentElement.classList.add('modal-open');
    document.body.classList.add('modal-open');

    modal.querySelector('[data-worlds-modal-close]').focus();
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeCharacterModal();
  });

  /* -------------------------------------------------------------------- character rotation */

  function createCharacterController(group) {
    var characters = Array.prototype.slice.call(group.querySelectorAll('[data-worlds-character]'));
    var selectableIndexes = characters
      .map(function (character, index) {
        return character.dataset.hasDescription === 'true' ? index : -1;
      })
      .filter(function (index) {
        return index !== -1;
      });

    var interval = null;
    var currentIndex = selectableIndexes.length ? selectableIndexes[0] : 0;
    var hovered = false;

    function setActive(index) {
      currentIndex = index;
      characters.forEach(function (character, characterIndex) {
        character.classList.toggle(
          'is-active',
          characterIndex === currentIndex && character.dataset.hasDescription === 'true'
        );
      });
    }

    function stop() {
      if (!interval) return;
      window.clearInterval(interval);
      interval = null;
    }

    function start() {
      if (interval || !selectableIndexes.length || window.innerWidth > MOBILE_WIDTH) return;

      interval = window.setInterval(function () {
        var currentPosition = selectableIndexes.indexOf(currentIndex);
        var nextPosition =
          currentPosition === -1 || currentPosition === selectableIndexes.length - 1 ? 0 : currentPosition + 1;
        setActive(selectableIndexes[nextPosition]);
      }, CHARACTER_TIMER);
    }

    characters.forEach(function (character, index) {
      character.addEventListener('mouseenter', function () {
        if (character.dataset.hasDescription !== 'true') return;
        hovered = true;
        stop();
        setActive(index);
      });

      character.addEventListener('mouseleave', function () {
        hovered = false;
        if (window.innerWidth <= MOBILE_WIDTH) start();
        else setActive(-1);
      });

      character.addEventListener('click', function () {
        if (character.dataset.hasDescription !== 'true') return;
        var nameEl = character.querySelector('.hc-worlds-character__name');
        pushDataLayer({
          event: 'universe_character_select',
          name: nameEl ? nameEl.textContent.trim() : character.dataset.characterName
        });
        openCharacterModal(character);
      });
    });

    setActive(window.innerWidth <= MOBILE_WIDTH ? currentIndex : -1);

    return {
      activate: function () {
        if (!hovered && window.innerWidth <= MOBILE_WIDTH) {
          setActive(currentIndex);
          start();
        }
      },
      deactivate: function () {
        stop();
        setActive(-1);
      },
      resize: function () {
        stop();
        if (window.innerWidth <= MOBILE_WIDTH) {
          setActive(currentIndex);
          start();
        } else if (!hovered) {
          setActive(-1);
        }
      }
    };
  }

  /* ----------------------------------------------------------------------------------- init */

  function initWorlds(root) {
    if (root.dataset.worldsInitialized === 'true') return;
    root.dataset.worldsInitialized = 'true';

    var configEl = root.querySelector('[data-worlds-config]');
    var config = (configEl && parseJson(configEl.textContent)) || {};
    var panelsEl = root.querySelector('[data-worlds-panels]');
    var navEl = root.querySelector('.hc-worlds-collections');
    var titleEl = root.querySelector('[data-worlds-title-set]');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-worlds-collection-trigger]'));

    if (!panelsEl || !triggers.length) return;

    var panelRequests = new Map();
    var swipers = new Map();
    var lottieAnimations = new Map();
    var characterControllers = new Map();

    // The server no longer always renders the first world, so read which one it did render
    // rather than assuming the first nav item.
    var renderedPanel = panelsEl.querySelector('[data-worlds-collection-panel]');
    var renderedHandle = (renderedPanel && renderedPanel.dataset.collectionHandle) || '';
    var collectionRoute = root.hasAttribute('data-worlds-collection-route');

    var activeHandle = renderedHandle || triggers[0].dataset.collectionHandle;
    var activeSetIndex = 0;
    var requestToken = 0;

    // Which nav animation should be open. Deliberately separate from activeHandle: the animation
    // reacts to the click immediately, while activeHandle only advances once the panel resolves.
    var desiredLottieHandle = activeHandle;
    var lottieOpen = new Map();

    /* ------------------------------------------------------------------------- dom lookups */

    function getTrigger(handle) {
      return (
        triggers.filter(function (trigger) {
          return trigger.dataset.collectionHandle === handle;
        })[0] || null
      );
    }

    function getPanel(handle) {
      return (
        Array.prototype.slice
          .call(panelsEl.querySelectorAll('[data-worlds-collection-panel]'))
          .filter(function (panel) {
            return panel.dataset.collectionHandle === handle;
          })[0] || null
      );
    }

    function getSetPanels(handle) {
      var panel = getPanel(handle);
      return panel ? Array.prototype.slice.call(panel.querySelectorAll('[data-worlds-set-panel]')) : [];
    }

    function getSetDescriptions(handle) {
      var panel = getPanel(handle);
      return panel ? Array.prototype.slice.call(panel.querySelectorAll('[data-worlds-set-description]')) : [];
    }

    /* ---------------------------------------------------------------------- panel fetching */

    function requestPanel(trigger) {
      var handle = trigger.dataset.collectionHandle;
      if (panelRequests.has(handle)) return panelRequests.get(handle);

      var url = trigger.dataset.panelUrl;
      if (!url) return Promise.resolve(null);

      var request = fetchSectionDocument(url)
        .then(function (doc) {
          return doc.querySelector('[data-worlds-collection-panel]');
        })
        .catch(function (error) {
          // Drop the cache entry so a second click retries instead of failing forever.
          panelRequests.delete(handle);
          throw error;
        });

      panelRequests.set(handle, request);
      return request;
    }

    function hydratePanel(panel) {
      panel.hidden = true;
      panel.style.display = 'none';

      if (config.productsHeading) {
        panel.querySelectorAll('[data-worlds-products-title]').forEach(function (heading) {
          if (!heading.textContent.trim()) heading.textContent = config.productsHeading;
        });
      }

      // Anything the theme binds globally (quick shop, lazy images, currency) can re-run here.
      document.dispatchEvent(
        new CustomEvent('hc:worlds:panel-loaded', { bubbles: true, detail: { panel: panel, section: root } })
      );
    }

    function ensurePanel(trigger) {
      var handle = trigger.dataset.collectionHandle;
      var existing = getPanel(handle);
      if (existing) return Promise.resolve(existing);

      return requestPanel(trigger).then(function (node) {
        if (!node) return null;

        // Another activation may have appended it while this request was in flight.
        var already = getPanel(handle);
        if (already) return already;

        var imported = document.importNode(node, true);
        panelsEl.appendChild(imported);
        hydratePanel(imported);
        return imported;
      });
    }

    /* ------------------------------------------------------------------------- nav + lottie */

    function setCollectionTriggerState(handle) {
      triggers.forEach(function (trigger) {
        var title = trigger.querySelector('.hc-worlds-collection__title');
        var active = trigger.dataset.collectionHandle === handle;
        var color = normalizeColor(trigger.dataset.collectionColor);

        trigger.classList.toggle('is-active', active);
        if (title) title.style.color = active && color ? color : '';
      });
    }

    // Drive every mounted animation towards "open iff it is the desired one". Idempotent, so it
    // is safe to call from a click, from a superseded click, from a failure, or on mount — and it
    // cannot leave an earlier collection open the way pairwise open/close could.
    function syncLottie() {
      lottieAnimations.forEach(function (animation, handle) {
        var shouldOpen = handle === desiredLottieHandle;
        if (lottieOpen.get(handle) === shouldOpen) return;

        lottieOpen.set(handle, shouldOpen);
        animation.setDirection(shouldOpen ? 1 : -1);
        animation.play();
      });
    }

    function setLottieOpen(handle) {
      desiredLottieHandle = handle;
      syncLottie();
    }

    function mountLottie(lottie, trigger) {
      var container = trigger.querySelector('[data-worlds-lottie-container]');
      if (!container || container.dataset.worldsLottieMounted) return;

      // Claim the container now rather than after the await: two calls in the same tick would
      // otherwise both pass the guard and stack two SVGs in one icon, one of them stuck open.
      container.dataset.worldsLottieMounted = 'pending';

      fetchAnimationData(trigger).then(function (animationData) {
        if (!animationData) {
          delete container.dataset.worldsLottieMounted;
          return;
        }

        container.dataset.worldsLottieMounted = 'true';
        container.textContent = '';

        var animation = lottie.loadAnimation({
          container: container,
          renderer: 'svg',
          autoplay: false,
          loop: false,
          animationData: animationData
        });
        animation.setSpeed(1.5);
        lottieAnimations.set(trigger.dataset.collectionHandle, animation);
        lottieOpen.set(trigger.dataset.collectionHandle, false);

        // Animations mount at unpredictable times; the desired state is the authority.
        syncLottie();
      });
    }

    function initLottie() {
      loadLottieLib(root.dataset.worldsLottieLib)
        .then(function (lottie) {
          if (!lottie) return;

          var active = getTrigger(desiredLottieHandle);
          if (active) mountLottie(lottie, active);

          onIdle(function () {
            triggers.forEach(function (trigger) {
              if (trigger !== active) mountLottie(lottie, trigger);
            });
          });
        })
        .catch(function () {
          /* nav simply renders without animations */
        });
    }

    /* ---------------------------------------------------------------------------- carousel */

    function setRealActiveSlide(swiperEl, index) {
      swiperEl.querySelectorAll('[data-worlds-set-slide]').forEach(function (slide, slideIndex) {
        slide.classList.toggle('is-worlds-real-active', slideIndex === index);
      });
    }

    function getSwiper(handle) {
      if (swipers.has(handle)) return swipers.get(handle);

      var panel = getPanel(handle);
      var swiperEl = panel ? panel.querySelector('[data-worlds-sets-swiper]') : null;
      if (!swiperEl || !window.Swiper) return null;

      var swiper = new window.Swiper(swiperEl, {
        centeredSlides: true,
        effect: 'coverflow',
        grabCursor: true,
        preventClicks: true,
        preventClicksPropagation: true,
        initialSlide: activeSetIndex,
        coverflowEffect: {
          rotate: 0,
          scale: 0.7,
          depth: 350,
          slideShadows: false
        },
        pagination: {
          el: panel.querySelector('[data-worlds-pagination]'),
          clickable: true,
          bulletClass: 'hc-slider-bullet hc-custom-world-slider-bullet',
          bulletActiveClass: 'hc-slider-bullet-active',
          dynamicBullets: swiperEl.querySelectorAll('.swiper-slide').length > 5,
          renderBullet: function (_index, className) {
            return (
              '<button type="button" name="Pagination bullet" aria-label="Pagination bullet" class="' +
              className +
              '"></button>'
            );
          }
        },
        navigation: {
          prevEl: panel.querySelector('[data-worlds-prev]'),
          nextEl: panel.querySelector('[data-worlds-next]')
        },
        on: {
          activeIndexChange: function (instance) {
            setRealActiveSlide(swiperEl, instance.activeIndex);
          },
          slideChangeTransitionEnd: function (instance) {
            setActiveSet(handle, instance.activeIndex, true);
          },
          slideResetTransitionEnd: function (instance) {
            setActiveSet(handle, instance.activeIndex, true);
          }
        }
      });

      swiperEl.classList.add('is-worlds-swiper-ready');
      setRealActiveSlide(swiperEl, activeSetIndex);
      swipers.set(handle, swiper);
      return swiper;
    }

    /* ------------------------------------------------------------------------ active state */

    function setActiveSet(handle, setIndex, shouldTrack) {
      activeSetIndex = setIndex || 0;

      getSetDescriptions(handle).forEach(function (description) {
        var active = Number(description.dataset.setIndex) === activeSetIndex;
        description.hidden = !active;
        description.style.display = active ? '' : 'none';
      });

      getSetPanels(handle).forEach(function (panel, panelIndex) {
        var active = panelIndex === activeSetIndex;
        panel.hidden = !active;

        panel.querySelectorAll('[data-worlds-characters]').forEach(function (group) {
          if (!characterControllers.has(group)) characterControllers.set(group, createCharacterController(group));
          var controller = characterControllers.get(group);
          if (active) controller.activate();
          else controller.deactivate();
        });
      });

      var activePanel = getSetPanels(handle)[activeSetIndex];
      var trigger = getTrigger(handle);

      // Now that this set has a layout box, let its images actually load.
      revealImages(activePanel);

      if (titleEl && activePanel && activePanel.dataset.setName) {
        titleEl.textContent = activePanel.dataset.setName;
      }

      if (shouldTrack && activePanel && trigger) {
        pushDataLayer({
          event: 'SelectSetFromWorlds',
          setId: activePanel.dataset.setId,
          productLineId: trigger.dataset.collectionId
        });
        writeUrl(trigger.dataset.collectionHandle, activePanel.dataset.setId);
      }
    }

    function showPanel(handle) {
      panelsEl.querySelectorAll('[data-worlds-collection-panel]').forEach(function (panel) {
        var active = panel.dataset.collectionHandle === handle;
        panel.hidden = !active;
        panel.style.display = active ? '' : 'none';

        // Images in this panel's still-hidden sets get observed, not loaded, so sets stay lazy.
        if (active) revealImages(panel);
      });
    }

    function setLoading(isLoading) {
      panelsEl.classList.toggle('is-worlds-loading', isLoading);
      if (isLoading) panelsEl.setAttribute('aria-busy', 'true');
      else panelsEl.removeAttribute('aria-busy');
    }

    function writeUrl(handle, setId) {
      var trigger = getTrigger(handle);

      if (collectionRoute && trigger && trigger.href) {
        // ?cid is redundant here: the path already names the world.
        updateUrl({ cid: '', sid: setId || '' }, new URL(trigger.href, window.location.origin).pathname);
        return;
      }

      updateUrl({ cid: handle, sid: setId || '' });
    }

    function resolveSetIndex(handle, setId) {
      if (!setId) return 0;

      var index = getSetPanels(handle).map(function (panel) {
        return panel.dataset.setId;
      }).indexOf(setId);

      return index >= 0 ? index : 0;
    }

    function setActiveCollection(handle, options) {
      var opts = options || {};
      var trigger = getTrigger(handle) || triggers[0];
      var nextHandle = trigger.dataset.collectionHandle;
      var token = ++requestToken;

      if (opts.resetSet) activeSetIndex = 0;

      // Highlight immediately so the nav responds before the panel arrives.
      setCollectionTriggerState(nextHandle);
      setLottieOpen(nextHandle);

      var cached = getPanel(nextHandle);
      var pending;

      if (cached) {
        pending = Promise.resolve(cached);
      } else {
        setLoading(true);
        pending = ensurePanel(trigger);
      }

      return pending
        .catch(function (error) {
          if (window.console) window.console.warn('[clay-universes] could not load panel', error);
          return null;
        })
        .then(function (panel) {
          if (token !== requestToken) return;
          setLoading(false);

          if (!panel) {
            // Keep the previously visible panel and put the nav back where it was.
            setCollectionTriggerState(activeHandle);
            setLottieOpen(activeHandle);
            return;
          }

          activeHandle = nextHandle;
          showPanel(nextHandle);

          if (opts.setId) activeSetIndex = resolveSetIndex(nextHandle, opts.setId);

          var swiper = getSwiper(nextHandle);
          if (swiper) {
            swiper.update();
            swiper.slideTo(activeSetIndex, 0);
            if (swiper.pagination) swiper.pagination.update();
            setRealActiveSlide(swiper.el, activeSetIndex);
          } else {
            panel.querySelectorAll('[data-worlds-set-slide]').forEach(function (slide, slideIndex) {
              slide.classList.toggle('is-fallback-active', slideIndex === activeSetIndex);
            });
          }

          setActiveSet(nextHandle, activeSetIndex, false);

          if (opts.track) {
            pushDataLayer({ event: 'SelectProductLineFromWorlds', productLineId: trigger.dataset.collectionId });
            writeUrl(nextHandle, '');
          }
        });
    }

    /* ---------------------------------------------------------------------------- bindings */

    function prefetch(trigger) {
      requestPanel(trigger).catch(function () {
        /* retried on click */
      });
    }

    triggers.forEach(function (trigger) {
      var warm = function () {
        prefetch(trigger);
      };

      trigger.addEventListener('pointerenter', warm, { once: true });
      trigger.addEventListener('focus', warm, { once: true });
      trigger.addEventListener('touchstart', warm, { once: true, passive: true });

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        setActiveCollection(trigger.dataset.collectionHandle, { track: true, resetSet: true });
      });
    });

    window.addEventListener('resize', function () {
      characterControllers.forEach(function (controller) {
        controller.resize();
      });
      swipers.forEach(function (swiper) {
        swiper.update();
      });
    });

    /* -------------------------------------------------------------------------------- boot */

    var params = new URLSearchParams(window.location.search);
    var initialCid = params.get('cid');
    var initialSid = params.get('sid');
    var initialTrigger =
      (initialCid &&
        triggers.filter(function (trigger) {
          return trigger.dataset.collectionHandle === initialCid || trigger.dataset.collectionId === initialCid;
        })[0]) ||
      getTrigger(renderedHandle) ||
      triggers[0];

    waitForSwiper().then(function () {
      setActiveCollection(initialTrigger.dataset.collectionHandle, { setId: initialSid });
    });

    // Lottie is only worth fetching once the nav is actually in view.
    var lottieStarted = false;

    function startLottie() {
      if (lottieStarted) return;
      lottieStarted = true;
      initLottie();
    }

    if (navEl && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          var visible = entries.some(function (entry) {
            return entry.isIntersecting;
          });
          if (!visible) return;
          observer.disconnect();
          startLottie();
        },
        { rootMargin: '400px' }
      );
      observer.observe(navEl);

      // Safety net: a zero-size or hidden viewport never reports an intersection, and the nav
      // must not be left without its animations because of it.
      window.setTimeout(function () {
        observer.disconnect();
        startLottie();
      }, 3000);
    } else {
      startLottie();
    }

    // Warm the neighbouring panels once the page is quiet.
    onIdle(function () {
      var index = triggers.indexOf(getTrigger(activeHandle));
      [triggers[index + 1], triggers[index - 1]].forEach(function (trigger) {
        if (trigger) prefetch(trigger);
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-worlds]').forEach(initWorlds);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-worlds]').forEach(initWorlds);
  });
})();
